/**
 * 场景匹配算法
 * 根据用户问卷结果从场景库中选择最适合的场景
 */
import scenarioLibrary from './scenarioData';

/**
 * 计算场景与用户问卷结果的匹配度
 * @param {Object} scenario - 场景对象
 * @param {Object} surveyData - 用户问卷结果
 * @returns {number} 匹配得分，分数越高表示匹配度越高
 */
const calculateMatchScore = (scenario, surveyData) => {
  let score = 0;
  
  // 性格特点匹配度评分
  if (surveyData.personalityTraits && scenario.tags.personality) {
    const personalityMatches = surveyData.personalityTraits.filter(trait => 
      scenario.tags.personality.includes(trait)
    );
    score += personalityMatches.length * 5; // 每个匹配的性格特点加5分
  }
  
  // 社交挑战匹配度评分
  if (surveyData.socialChallenges && scenario.tags.challenges) {
    const challengeMatches = surveyData.socialChallenges.filter(challenge => 
      scenario.tags.challenges.includes(challenge)
    );
    score += challengeMatches.length * 10; // 每个匹配的社交挑战加10分
  }
  
  // 改进领域匹配度评分
  if (surveyData.improvementAreas && scenario.tags.challenges) {
    const improvementMatches = surveyData.improvementAreas.filter(area => 
      scenario.tags.challenges.includes(area)
    );
    score += improvementMatches.length * 8; // 每个匹配的改进领域加8分
  }
  
  // 学习目标匹配度评分
  if (surveyData.learningGoals && scenario.tags.goals) {
    const goalMatches = surveyData.learningGoals.filter(goal => 
      scenario.tags.goals.includes(goal)
    );
    score += goalMatches.length * 15; // 每个匹配的学习目标加15分
  }
  
  // 场景类型偏好匹配度评分
  if (surveyData.preferredScenarios && scenario.tags.context) {
    const contextMatches = scenario.tags.context.includes(surveyData.preferredScenarios);
    if (contextMatches) score += 20; // 场景类型匹配加20分
    
    // 如果用户偏好"全方位混合"类型，所有场景都有额外加分
    if (surveyData.preferredScenarios === 'allRounded') {
      score += 10;
    }
  }
  
  // 根据职业调整匹配度
  if (surveyData.occupation) {
    // 为管理者和高管提高领导相关场景的分数
    if ((surveyData.occupation === 'manager' || surveyData.occupation === 'entrepreneur') && 
        scenario.tags.goals.includes('leadership')) {
      score += 12;
    }
    
    // 为学生提高学习和个人发展场景的分数
    if (surveyData.occupation === 'student' && scenario.tags.difficulty === 'beginner') {
      score += 8;
    }
    
    // 为专业人士提高职场相关场景的分数
    if (['professional', 'technical', 'creative'].includes(surveyData.occupation) && 
        scenario.tags.context.includes('workplace')) {
      score += 10;
    }
  }
  
  // 根据沟通风格调整匹配度
  if (surveyData.communicationStyle) {
    const communicationStyleMap = {
      'direct': ['direct', 'analytical'],
      'expressive': ['outgoing', 'creative'],
      'thoughtful': ['reserved', 'analytical'],
      'supportive': ['empathetic', 'supportive'],
      'analytical': ['analytical', 'practical']
    };
    
    const preferredStyles = communicationStyleMap[surveyData.communicationStyle] || [];
    const styleMatches = preferredStyles.filter(style => 
      scenario.tags.personality.includes(style)
    );
    
    score += styleMatches.length * 7; // 每个匹配的沟通风格加7分
  }
  
  // 根据年龄组调整匹配度 (仅微调)
  if (surveyData.ageGroup) {
    // 为年轻用户增加社交场景相关分数
    if (surveyData.ageGroup === '18-24' && scenario.tags.context.includes('socialSettings')) {
      score += 5;
    }
    
    // 为职场中期用户增加职业发展场景相关分数
    if (['25-34', '35-44'].includes(surveyData.ageGroup) && 
        scenario.tags.goals.includes('careerAdvancement')) {
      score += 5;
    }
    
    // 为资深用户增加领导和教导相关场景分数
    if (['45-54', '55+'].includes(surveyData.ageGroup) && 
        (scenario.tags.goals.includes('leadership') || scenario.tags.goals.includes('mentoring'))) {
      score += 5;
    }
  }
  
  return score;
};

/**
 * 根据用户的问卷回答选择最匹配的6个场景
 * @param {Object} surveyData - 用户问卷数据
 * @returns {Array} 返回6个最匹配的场景对象
 */
export const selectCustomScenarios = (surveyData) => {
  // 对场景库中的每个场景计算匹配得分
  const scoredScenarios = scenarioLibrary.map(scenario => ({
    ...scenario,
    matchScore: calculateMatchScore(scenario, surveyData)
  }));
  
  // 按匹配得分降序排序
  const sortedScenarios = scoredScenarios.sort((a, b) => b.matchScore - a.matchScore);
  
  // 选择前6个得分最高的场景
  const selectedScenarios = sortedScenarios.slice(0, 6);
  
  // 确保选择的场景涵盖不同难度级别
  const difficultyCounts = {
    beginner: selectedScenarios.filter(s => s.tags.difficulty === 'beginner').length,
    intermediate: selectedScenarios.filter(s => s.tags.difficulty === 'intermediate').length,
    advanced: selectedScenarios.filter(s => s.tags.difficulty === 'advanced').length
  };
  
  // 如果某个难度级别完全没有场景，尝试进行平衡调整
  if (difficultyCounts.beginner === 0 || difficultyCounts.intermediate === 0 || difficultyCounts.advanced === 0) {
    // 寻找需要补充的难度级别
    const missingDifficulties = Object.keys(difficultyCounts).filter(diff => difficultyCounts[diff] === 0);
    
    // 对于每个缺失的难度级别，尝试替换一个过多的难度级别的场景
    missingDifficulties.forEach(missingDiff => {
      // 找出数量最多的难度级别
      const maxDiff = Object.keys(difficultyCounts).reduce((a, b) => 
        difficultyCounts[a] > difficultyCounts[b] ? a : b
      );
      
      if (difficultyCounts[maxDiff] > 1) { // 确保有足够多的场景可以替换
        // 在已排序的场景中查找第一个符合缺失难度级别的场景
        const replacementScenario = sortedScenarios.find(s => 
          s.tags.difficulty === missingDiff && !selectedScenarios.includes(s)
        );
        
        if (replacementScenario) {
          // 找到最低分的过多难度级别的场景进行替换
          const scenarioToReplace = selectedScenarios
            .filter(s => s.tags.difficulty === maxDiff)
            .sort((a, b) => a.matchScore - b.matchScore)[0];
          
          const replaceIndex = selectedScenarios.indexOf(scenarioToReplace);
          if (replaceIndex !== -1) {
            selectedScenarios[replaceIndex] = replacementScenario;
            
            // 更新难度计数
            difficultyCounts[missingDiff]++;
            difficultyCounts[maxDiff]--;
          }
        }
      }
    });
  }
  
  // 确保场景上下文多样性
  const contextCounts = {};
  selectedScenarios.forEach(s => {
    s.tags.context.forEach(ctx => {
      contextCounts[ctx] = (contextCounts[ctx] || 0) + 1;
    });
  });
  
  // 如果某个上下文占比过高（超过50%），尝试替换一个场景增加多样性
  const dominantContext = Object.keys(contextCounts).find(ctx => 
    contextCounts[ctx] > selectedScenarios.length * 0.5
  );
  
  if (dominantContext) {
    // 找出分数最低的属于主导上下文的场景
    const scenarioToReplace = selectedScenarios
      .filter(s => s.tags.context.includes(dominantContext))
      .sort((a, b) => a.matchScore - b.matchScore)[0];
    
    // 在未被选中的场景中寻找不属于主导上下文且得分最高的场景
    const replacementScenario = sortedScenarios.find(s => 
      !selectedScenarios.includes(s) && !s.tags.context.includes(dominantContext)
    );
    
    if (replacementScenario && scenarioToReplace) {
      const replaceIndex = selectedScenarios.indexOf(scenarioToReplace);
      if (replaceIndex !== -1) {
        selectedScenarios[replaceIndex] = replacementScenario;
      }
    }
  }
  
  return selectedScenarios;
};

export default selectCustomScenarios; 